/**
 * The listing's marketing state chip (listing-marketing-hub BR-2, BR-4), on the listing header and
 * each listings index row. Only an active listing gets one: "Not marketed yet" (a link to its
 * Marketing tab, never dismissible) until a post is recorded, then "N posts live".
 */
import { Link } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { isMarketed, postedLabel } from '../../lib/listing-marketing';
import type { Listing } from '../../lib/leasing.api';

interface MarketingStateChipProps {
  listingId: string;
  status: Listing['status'];
  /** Posted external post records. */
  postedCount: number;
  /** `header`: on the listing's own page (a hash link to its tab); `index`: a row of the listings index. */
  variant: 'header' | 'index';
}

const LINK_CLASS = 'inline-flex min-h-[44px] items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function MarketingStateChip({ listingId, status, postedCount, variant }: MarketingStateChipProps) {
  if (status !== 'active') return null;
  const testId = variant === 'header' ? 'listing-marketing-chip' : `listing-marketing-chip-${listingId}`;

  if (isMarketed(postedCount)) {
    return (
      <Badge variant="info" data-testid={testId} data-state="marketed">
        {postedLabel(postedCount)}
      </Badge>
    );
  }

  if (variant === 'header') {
    // A hash link: the section tabs follow `hashchange`, which a router navigation does not fire.
    return (
      <a href="#marketing" className={LINK_CLASS} data-testid={testId} data-state="not-marketed">
        <Badge variant="warning">Not marketed yet · Open Marketing</Badge>
      </a>
    );
  }
  return (
    <Link
      to={{ pathname: `/leasing/listings/${listingId}`, hash: '#marketing' }}
      onClick={(e) => e.stopPropagation()}
      className={LINK_CLASS}
      data-testid={testId}
      data-state="not-marketed"
    >
      <Badge variant="warning">Not marketed yet</Badge>
    </Link>
  );
}
